import { Controller } from "@hotwired/stimulus";
import { application } from "src/stimulus/application";
import {
  fetchNumberOfRentalPropertiesConstructed,
  RentalPropertiesConstructedPoint,
} from "src/site/estat";
import { ChartSpec, debounce, isSp, renderChart } from "src/site/recharts";

// Site::PrefectureArea::NumberOfRentalPropertiesConstructed::Component の Stimulus コントローラ。
// landIeul: hooks/eStat/useNumberOfRentalPropertiesConstructed.ts + NumberOfRentalPropertiesGraph。

class SitePrefectureAreaNumberOfRentalPropertiesConstructedController extends Controller<HTMLElement> {
  static targets = [
    "graph",
    "description0",
    "description1",
    "description2",
    "description3",
  ];
  static values = { areaCode: String };

  declare readonly graphTarget: HTMLElement;
  declare readonly description0Target: HTMLElement;
  declare readonly description1Target: HTMLElement;
  declare readonly description2Target: HTMLElement;
  declare readonly description3Target: HTMLElement;
  declare readonly areaCodeValue: string;

  private graphData: RentalPropertiesConstructedPoint[] = [];
  private onResize?: () => void;

  connect(): void {
    this.onResize = debounce(() => this.draw());
    window.addEventListener("resize", this.onResize);
    void this.load();
  }

  disconnect(): void {
    if (this.onResize) window.removeEventListener("resize", this.onResize);
  }

  private async load(): Promise<void> {
    const result = await fetchNumberOfRentalPropertiesConstructed(this.areaCodeValue);
    if (!result) return;
    this.graphData = result.graphData;
    // DESCRIPTIONS の並び: [木造↑非木造↑, 木造↑非木造↓, 木造↓非木造↑, 木造↓非木造↓]
    const index = result.isIncreaseTotalWooden
      ? (result.isIncreaseTotalNotWooden ? 0 : 1)
      : (result.isIncreaseTotalNotWooden ? 2 : 3);
    [
      this.description0Target,
      this.description1Target,
      this.description2Target,
      this.description3Target,
    ].forEach((el, i) => {
      el.hidden = i !== index;
    });
    this.draw();
  }

  private draw(): void {
    if (this.graphData.length === 0) return;
    renderChart(this.graphTarget, this.spec());
  }

  private spec(): ChartSpec {
    const sp = isSp();
    const tickFontSize = sp ? "10px" : "12px";
    const strokeWidth = sp ? 2 : 3;
    return {
      kind: "line",
      data: this.graphData as unknown as ChartSpec["data"],
      xKey: "year",
      xPadding: { left: 20, right: 20 },
      xTickFontSize: tickFontSize,
      yTickFontSize: tickFontSize,
      yUnit: "戸",
      yLocaleString: true,
      curve: "monotone",
      legendWrapperStyle: {
        "font-size": sp ? "10px" : "15px",
        "margin-left": "20px",
        "line-height": "40px",
      },
      series: [
        { key: "totalConstructed", name: "全体", stroke: "#FF367F", fill: "#FF367F", strokeWidth, unit: "戸" },
        { key: "totalWooden", name: "木造", stroke: "#66CDAA", fill: "#66CDAA", strokeWidth, unit: "戸" },
        { key: "totalNotWooden", name: "非木造", stroke: "#2C7CFF", fill: "#2C7CFF", strokeWidth, unit: "戸" },
      ],
      containerStyle: "width:100%;height:100%;min-width:0",
    };
  }
}

application.register(
  "site--prefecture-area--number-of-rental-properties-constructed",
  SitePrefectureAreaNumberOfRentalPropertiesConstructedController,
);
